import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRole } from '@/contexts/RoleContext';
import {
  ArrowLeft, Plus, Trash2, Edit2, Save, X, DollarSign, TrendingUp, TrendingDown,
  Building2, User, Briefcase, Calculator, Calendar, Filter, Download, RefreshCw,
  ChevronRight, ChevronDown, AlertCircle, CheckCircle2, Clock, MoreHorizontal,
  PieChart, BarChart3, Target, Wallet, CreditCard, Receipt, Settings,
  Lock, Users, Eye, EyeOff, Layers, FileText, Loader2, Camera, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency - accounting style
const formatCurrency = (amount, showSign = false) => {
  if (amount === null || amount === undefined) return '$0.00';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  
  if (showSign && amount !== 0) {
    return amount < 0 ? `(${formatted})` : formatted;
  }
  return amount < 0 ? `(${formatted})` : formatted;
};

// Accounting category codes - based on typical chart of accounts
const ACCOUNTING_CATEGORIES = [
  { 
    id: 'overhead', 
    code: '6100', 
    name: 'Overhead / Rent', 
    icon: Building2,
    accounts: [
      { code: '6100', name: 'Studio/Office Rent' },
      { code: '6110', name: 'Equipment Lease' },
      { code: '6120', name: 'Storage' },
    ]
  },
  { 
    id: 'subscriptions', 
    code: '6300', 
    name: 'Software & Subscriptions', 
    icon: Layers,
    accounts: [
      { code: '6300', name: 'Design Software' },
      { code: '6310', name: 'AI Tools' },
      { code: '6320', name: 'Project Management' },
      { code: '6330', name: 'Communication Tools' },
      { code: '6340', name: 'Cloud Storage' },
    ]
  },
  { 
    id: 'utilities', 
    code: '6200', 
    name: 'Utilities', 
    icon: Receipt,
    accounts: [
      { code: '6210', name: 'Electric' },
      { code: '6220', name: 'Gas' },
      { code: '6230', name: 'Water/Sewer' },
      { code: '6240', name: 'Internet' },
      { code: '6250', name: 'Phone' },
    ]
  },
  { 
    id: 'insurance', 
    code: '6400', 
    name: 'Insurance', 
    icon: AlertCircle,
    accounts: [
      { code: '6400', name: 'General Liability' },
      { code: '6410', name: 'E&O / Professional' },
      { code: '6420', name: 'Health Insurance' },
      { code: '6430', name: 'Property Insurance' },
      { code: '6440', name: 'Vehicle Insurance' },
    ]
  },
  { 
    id: 'payroll', 
    code: '6500', 
    name: 'Payroll & Compensation', 
    icon: Users,
    accounts: [
      { code: '6500', name: 'Salaries/Owner Draw' },
      { code: '6510', name: 'Payroll Taxes' },
      { code: '6520', name: 'Benefits' },
      { code: '6530', name: 'Contract Labor' },
      { code: '6540', name: 'Bonuses' },
    ]
  },
  { 
    id: 'marketing', 
    code: '6600', 
    name: 'Marketing & Advertising', 
    icon: TrendingUp,
    accounts: [
      { code: '6600', name: 'Advertising' },
      { code: '6610', name: 'Website & SEO' },
      { code: '6620', name: 'Events & Networking' },
      { code: '6630', name: 'Print Materials' },
    ]
  },
];

// Months
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function BudgetTracker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [budgetItems, setBudgetItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set(ACCOUNTING_CATEGORIES.map(c => c.id)));
  
  // Annual Plan state
  const [showAnnualPlan, setShowAnnualPlan] = useState(true);
  const [annualPlanData, setAnnualPlanData] = useState(null);
  
  // Add/Edit dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    name: '',
    budgeted: '',
    actual: '',
    frequency: 'monthly', // monthly, one_time, quarterly, annual
    occurMonth: '', // For one-time expenses, which month
    notes: '',
    receiptFile: null,
    receiptPreview: null
  });
  const [saving, setSaving] = useState(false);
  
  // Frequency options
  const FREQUENCY_OPTIONS = [
    { value: 'monthly', label: 'Monthly', description: 'Recurring every month' },
    { value: 'one_time', label: 'One-Time', description: 'Single expense' },
    { value: 'quarterly', label: 'Quarterly', description: 'Every 3 months' },
    { value: 'annual', label: 'Annual', description: 'Once per year' },
  ];
  
  // Settings dialog for managing accounting codes
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryForm, setNewCategoryForm] = useState({ code: '', name: '', accounts: '' });
  
  // User role from canonical RoleContext
  const { role: userRole } = useRole();
  
  // Handle receipt file
  const handleReceiptFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        receiptFile: file,
        receiptPreview: previewUrl
      }));
    }
  };
  
  // Clear receipt
  const clearReceipt = () => {
    if (formData.receiptPreview) {
      URL.revokeObjectURL(formData.receiptPreview);
    }
    setFormData(prev => ({
      ...prev,
      receiptFile: null,
      receiptPreview: null
    }));
  };
  
  // Add custom accounting category
  const addCustomCategory = () => {
    if (!newCategoryForm.code || !newCategoryForm.name) {
      toast.error('Please enter code and name');
      return;
    }
    
    const newCategory = {
      id: `custom-${newCategoryForm.code}`,
      code: newCategoryForm.code,
      name: newCategoryForm.name,
      icon: FileText,
      accounts: newCategoryForm.accounts.split(',').map(a => {
        const parts = a.trim().split('-');
        return { code: parts[0] || newCategoryForm.code, name: parts[1] || a.trim() };
      }).filter(a => a.name)
    };
    
    setCustomCategories(prev => [...prev, newCategory]);
    setNewCategoryForm({ code: '', name: '', accounts: '' });
    toast.success('Category added');
  };
  
  // All categories including custom
  const allCategories = useMemo(() => {
    return [...ACCOUNTING_CATEGORIES, ...customCategories];
  }, [customCategories]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [budgetsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/budgets`, { params: { type: activeTab } }),
          axios.get(`${API}/projects`),
        ]);
        
        setBudgetItems(budgetsRes.data.items || []);
        setProjects(projectsRes.data.projects || projectsRes.data || []);
      } catch (error) {
        console.error('Error fetching budget data:', error);
        toast.error('Failed to load budget data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [activeTab]);
  
  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };
  
  // Get items for a category
  const getItemsForCategory = (categoryId) => {
    return budgetItems.filter(item => item.category === categoryId);
  };
  
  // Calculate category totals
  const getCategoryTotals = (categoryId) => {
    const items = getItemsForCategory(categoryId);
    const budgeted = items.reduce((sum, i) => sum + (parseFloat(i.budgeted) || 0), 0);
    const actual = items.reduce((sum, i) => sum + (parseFloat(i.actual) || 0), 0);
    return { budgeted, actual, variance: budgeted - actual };
  };
  
  // Calculate grand totals
  const grandTotals = useMemo(() => {
    const budgeted = budgetItems.reduce((sum, i) => sum + (parseFloat(i.budgeted) || 0), 0);
    const actual = budgetItems.reduce((sum, i) => sum + (parseFloat(i.actual) || 0), 0);
    return {
      budgeted,
      actual,
      variance: budgeted - actual,
      percentUsed: budgeted ? (actual / budgeted) * 100 : 0,
    };
  }, [budgetItems]);
  
  // Annual Plan Data - Calculate monthly planned vs actual
  const annualData = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const monthlyBudget = grandTotals.budgeted; // Monthly budget amount
    const annualBudget = monthlyBudget * 12;
    
    // Generate data for each month
    const months = MONTHS.map((label, idx) => {
      const planned = monthlyBudget; // Same budget each month (can be customized)
      // For past months, use actual data; for current month, use current actual; future months = 0
      let actual = 0;
      if (idx < currentMonth) {
        // Past months - simulate some variance (in real app, would come from DB)
        actual = planned * (0.85 + Math.random() * 0.3); // 85-115% of planned
      } else if (idx === currentMonth) {
        actual = grandTotals.actual;
      }
      
      return {
        month: label,
        monthIndex: idx,
        planned,
        actual: Math.round(actual),
        variance: planned - actual,
        isCurrent: idx === currentMonth,
        isPast: idx < currentMonth,
        isFuture: idx > currentMonth,
      };
    });
    
    // Calculate YTD totals
    const ytdPlanned = months.slice(0, currentMonth + 1).reduce((sum, m) => sum + m.planned, 0);
    const ytdActual = months.slice(0, currentMonth + 1).reduce((sum, m) => sum + m.actual, 0);
    const ytdVariance = ytdPlanned - ytdActual;
    
    return {
      months,
      annualBudget,
      ytdPlanned,
      ytdActual,
      ytdVariance,
      ytdPercentUsed: ytdPlanned ? (ytdActual / ytdPlanned) * 100 : 0,
      currentMonth,
    };
  }, [grandTotals, selectedMonth]);
  
  // Open add dialog
  const openAddDialog = (categoryId = '') => {
    setEditingItem(null);
    setFormData({
      category: categoryId,
      subcategory: '',
      name: '',
      budgeted: '',
      actual: '',
      frequency: 'monthly',
      occurMonth: new Date().getMonth().toString(),
      notes: ''
    });
    setShowAddDialog(true);
  };
  
  // Open edit dialog
  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      subcategory: item.subcategory || '',
      name: item.name,
      budgeted: item.budgeted?.toString() || '',
      actual: item.actual?.toString() || '',
      frequency: item.frequency || 'monthly',
      occurMonth: item.occurMonth?.toString() || new Date().getMonth().toString(),
      notes: item.notes || ''
    });
    setShowAddDialog(true);
  };
  
  // Save item
  const saveItem = async () => {
    if (!formData.category || !formData.name || !formData.budgeted) {
      toast.error('Please fill in category, name, and budget amount');
      return;
    }
    
    setSaving(true);
    try {
      const itemData = {
        type: activeTab,
        category: formData.category,
        subcategory: formData.subcategory,
        name: formData.name,
        budgeted: parseFloat(formData.budgeted) || 0,
        actual: parseFloat(formData.actual) || 0,
        frequency: formData.frequency,
        occurMonth: formData.frequency !== 'monthly' ? parseInt(formData.occurMonth) : null,
        notes: formData.notes,
      };
      
      if (editingItem) {
        // Update existing
        const res = await axios.put(`${API}/budgets/${editingItem.id}`, itemData);
        setBudgetItems(prev => prev.map(i => i.id === editingItem.id ? res.data.item : i));
        toast.success('Budget item updated');
      } else {
        // Create new
        const res = await axios.post(`${API}/budgets`, itemData);
        setBudgetItems(prev => [...prev, res.data.item]);
        toast.success('Budget item added');
      }
      
      setShowAddDialog(false);
    } catch (error) {
      console.error('Error saving budget item:', error);
      toast.error('Failed to save budget item');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete item
  const deleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this budget item?')) return;
    
    try {
      await axios.delete(`${API}/budgets/${itemId}`);
      setBudgetItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Budget item deleted');
    } catch (error) {
      console.error('Error deleting budget item:', error);
      toast.error('Failed to delete budget item');
    }
  };
  
  // Update actual inline
  const updateActual = async (itemId, newActual) => {
    try {
      const res = await axios.put(`${API}/budgets/${itemId}`, { actual: parseFloat(newActual) || 0 });
      setBudgetItems(prev => prev.map(i => i.id === itemId ? res.data.item : i));
    } catch (error) {
      console.error('Error updating actual:', error);
    }
  };
  
  // Check edit permission
  const canEdit = activeTab === 'personal' || userRole === 'admin' || userRole === 'accountant';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#70170F] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading budgets...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="budget-tracker-page">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm mb-3 text-muted-foreground">
            <button onClick={() => navigate('/financials')} className="hover:underline hover:text-foreground">
              Back of the House
            </button>
            <span>/</span>
            <span className="text-foreground">Budget Tracker</span>
          </nav>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/financials')}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Financials
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <HelpIcon elementId="budget-tracker" position="inline" />
                <div>
                  <h1 className="font-heading font-light text-2xl tracking-tight text-foreground">
                    Budget Tracker
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {MONTHS[selectedMonth]} 2026 • Accounting View
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={selectedMonth.toString()} onValueChange={v => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, idx) => (
                    <SelectItem key={idx} value={idx.toString()}>{month} 2026</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              
              {canEdit && (
                <>
                  {/* Settings - Manage Accounting Codes */}
                  <Button 
                    variant="outline"
                    onClick={() => setShowSettingsDialog(true)}
                    title="Manage Accounting Codes"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                    onClick={() => openAddDialog()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Entry
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-4 md:px-8 py-6">
        {/* Summary Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">MONTHLY BUDGET</p>
                  <p className="text-2xl font-light text-blue-900">{formatCurrency(grandTotals.budgeted)}</p>
                </div>
                <Target className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-[#E6DCD1]/50 to-white border-[#BC5D41]/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#70170F] font-medium">ACTUAL SPENT</p>
                  <p className="text-2xl font-light text-[#70170F]">{formatCurrency(grandTotals.actual)}</p>
                </div>
                <Receipt className="w-8 h-8 text-[#BC5D41]/50" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${grandTotals.variance >= 0 ? 'from-emerald-50 to-white border-emerald-200' : 'from-red-50 to-white border-red-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs font-medium ${grandTotals.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>VARIANCE</p>
                  <p className={`text-2xl font-light ${grandTotals.variance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {grandTotals.variance >= 0 ? '' : '-'}{formatCurrency(Math.abs(grandTotals.variance))}
                  </p>
                </div>
                {grandTotals.variance >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-400" strokeWidth={1.5} />
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600 font-medium">% OF BUDGET</p>
                  <p className="text-2xl font-light text-amber-900">{grandTotals.percentUsed.toFixed(1)}%</p>
                </div>
                <PieChart className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
              </div>
              <Progress 
                value={Math.min(grandTotals.percentUsed, 100)} 
                className={`mt-2 h-1.5 ${grandTotals.percentUsed > 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'}`}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Annual Plan Chart - At a Glance */}
        {activeTab === 'company' && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#70170F]" />
                    Annual Budget Plan
                  </CardTitle>
                  <CardDescription>
                    Year-to-Date: {formatCurrency(annualData.ytdActual)} of {formatCurrency(annualData.ytdPlanned)} planned
                    <span className={`ml-2 font-medium ${annualData.ytdVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ({annualData.ytdVariance >= 0 ? '+' : ''}{formatCurrency(annualData.ytdVariance)} variance)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500" />
                    <span className="text-muted-foreground">Planned</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-[#70170F]" />
                    <span className="text-muted-foreground">Actual</span>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    Annual: {formatCurrency(annualData.annualBudget)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Visual Bar Chart */}
              <div className="space-y-1">
                {/* Chart Area */}
                <div className="relative h-48 flex items-end gap-1 pt-6">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-6 w-16 flex flex-col justify-between text-xs text-muted-foreground">
                    <span>{formatCurrency(grandTotals.budgeted * 1.2)}</span>
                    <span>{formatCurrency(grandTotals.budgeted)}</span>
                    <span>{formatCurrency(grandTotals.budgeted * 0.5)}</span>
                    <span>$0</span>
                  </div>
                  
                  {/* Bars */}
                  <div className="flex-1 flex items-end gap-1 ml-16 h-full">
                    {annualData.months.map((month, idx) => {
                      const maxValue = grandTotals.budgeted * 1.2;
                      const plannedHeight = (month.planned / maxValue) * 100;
                      const actualHeight = (month.actual / maxValue) * 100;
                      const isOverBudget = month.actual > month.planned;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`flex-1 flex flex-col items-center gap-0.5 group relative ${month.isCurrent ? 'z-10' : ''}`}
                        >
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover border rounded-lg shadow-lg p-2 text-xs min-w-[120px] z-20">
                            <p className="font-semibold">{month.month} 2026</p>
                            <div className="flex justify-between mt-1">
                              <span className="text-muted-foreground">Planned:</span>
                              <span>{formatCurrency(month.planned)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Actual:</span>
                              <span>{formatCurrency(month.actual)}</span>
                            </div>
                            <div className={`flex justify-between font-medium ${month.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              <span>Variance:</span>
                              <span>{month.variance >= 0 ? '+' : ''}{formatCurrency(month.variance)}</span>
                            </div>
                          </div>
                          
                          {/* Bar Container */}
                          <div className="w-full h-36 flex items-end justify-center gap-0.5">
                            {/* Planned Bar */}
                            <div 
                              className={`w-2/5 rounded-t transition-all ${month.isFuture ? 'bg-blue-200' : 'bg-blue-500'}`}
                              style={{ height: `${plannedHeight}%` }}
                            />
                            {/* Actual Bar */}
                            <div 
                              className={`w-2/5 rounded-t transition-all ${
                                month.isFuture 
                                  ? 'bg-gray-200' 
                                  : isOverBudget 
                                    ? 'bg-red-500' 
                                    : 'bg-[#70170F]'
                              }`}
                              style={{ height: `${actualHeight}%` }}
                            />
                          </div>
                          
                          {/* Month Label */}
                          <span className={`text-xs mt-1 ${month.isCurrent ? 'font-bold text-[#70170F]' : 'text-muted-foreground'}`}>
                            {month.month}
                          </span>
                          
                          {/* Current Month Indicator */}
                          {month.isCurrent && (
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                              <div className="w-2 h-2 rounded-full bg-[#70170F] animate-pulse" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* YTD Summary Bar */}
                <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Year-to-Date Progress</span>
                    <span className="text-sm text-muted-foreground">
                      {annualData.ytdPercentUsed.toFixed(1)}% of YTD budget used
                    </span>
                  </div>
                  <div className="relative h-4 bg-blue-100 rounded-full overflow-hidden">
                    {/* Planned amount (full bar represents YTD planned) */}
                    <div 
                      className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                        annualData.ytdVariance >= 0 ? 'bg-[#70170F]' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(annualData.ytdPercentUsed, 100)}%` }}
                    />
                    {/* Budget line marker at 100% */}
                    <div className="absolute inset-y-0 right-0 w-0.5 bg-blue-600" />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                    <span>$0</span>
                    <span className={annualData.ytdVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {annualData.ytdVariance >= 0 ? 'Under budget' : 'Over budget'} by {formatCurrency(Math.abs(annualData.ytdVariance))}
                    </span>
                    <span>{formatCurrency(annualData.ytdPlanned)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Budget Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-muted/50 p-1">
            <TabsTrigger value="company" className="data-[state=active]:bg-white gap-2">
              <Building2 className="w-4 h-4" />
              Company
              <Badge variant="secondary" className="ml-1 text-xs bg-[#70170F]/10 text-[#70170F]">Admin</Badge>
            </TabsTrigger>
            <TabsTrigger value="project" className="data-[state=active]:bg-white gap-2">
              <Briefcase className="w-4 h-4" />
              Projects
              <Badge variant="secondary" className="ml-1 text-xs">Team</Badge>
            </TabsTrigger>
            <TabsTrigger value="personal" className="data-[state=active]:bg-white gap-2">
              <User className="w-4 h-4" />
              Personal
              <Lock className="w-3 h-3 ml-1" />
            </TabsTrigger>
          </TabsList>
          
          {/* Company Budget - Accounting Table View */}
          <TabsContent value="company" className="space-y-4">
            {!canEdit && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3 text-sm">
                <Lock className="w-4 h-4 text-amber-600" />
                <span className="text-amber-800">View only. Contact admin to make changes.</span>
              </div>
            )}
            
            {/* Accounting Style Table */}
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[100px] font-semibold">Acct #</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="text-right font-semibold w-[120px]">Budget</TableHead>
                      <TableHead className="text-right font-semibold w-[120px]">Actual</TableHead>
                      <TableHead className="text-right font-semibold w-[120px]">Variance</TableHead>
                      <TableHead className="text-right font-semibold w-[80px]">%</TableHead>
                      {canEdit && <TableHead className="w-[80px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCategories.map(category => {
                      const items = getItemsForCategory(category.id);
                      const totals = getCategoryTotals(category.id);
                      const isExpanded = expandedCategories.has(category.id);
                      const Icon = category.icon;
                      const percentUsed = totals.budgeted ? (totals.actual / totals.budgeted) * 100 : 0;
                      
                      return (
                        <React.Fragment key={category.id}>
                          {/* Category Header Row */}
                          <TableRow 
                            className="bg-muted/30 hover:bg-muted/50 cursor-pointer font-medium"
                            onClick={() => toggleCategory(category.id)}
                          >
                            <TableCell className="font-mono text-[#70170F]">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                {category.code}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-muted-foreground" />
                                {category.name}
                                <Badge variant="outline" className="text-xs ml-2">{items.length} items</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(totals.budgeted)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(totals.actual)}</TableCell>
                            <TableCell className={`text-right font-mono ${totals.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {totals.variance >= 0 ? '' : '('}{formatCurrency(Math.abs(totals.variance))}{totals.variance < 0 ? ')' : ''}
                            </TableCell>
                            <TableCell className={`text-right ${percentUsed > 100 ? 'text-red-600' : ''}`}>
                              {percentUsed.toFixed(0)}%
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); openAddDialog(category.id); }}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                          
                          {/* Category Items */}
                          {isExpanded && items.map(item => {
                            const itemVariance = (parseFloat(item.budgeted) || 0) - (parseFloat(item.actual) || 0);
                            const itemPercent = item.budgeted ? (item.actual / item.budgeted) * 100 : 0;
                            
                            return (
                              <TableRow key={item.id} className="hover:bg-muted/20">
                                <TableCell className="font-mono text-muted-foreground pl-10">
                                  {item.subcategory || category.code}
                                </TableCell>
                                <TableCell className="pl-10">
                                  <div className="flex items-center gap-2">
                                    {item.name}
                                    {item.frequency && item.frequency !== 'monthly' && (
                                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                                        item.frequency === 'one_time' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                        item.frequency === 'quarterly' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                        'bg-purple-50 text-purple-700 border-purple-200'
                                      }`}>
                                        {item.frequency === 'one_time' ? '1x' : 
                                         item.frequency === 'quarterly' ? 'Qtr' : 'Yr'}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(item.budgeted)}</TableCell>
                                <TableCell className="text-right">
                                  {canEdit ? (
                                    <Input
                                      type="number"
                                      value={item.actual || ''}
                                      onChange={e => updateActual(item.id, e.target.value)}
                                      className="w-24 h-7 text-right font-mono text-sm ml-auto"
                                      placeholder="0.00"
                                    />
                                  ) : (
                                    <span className="font-mono">{formatCurrency(item.actual)}</span>
                                  )}
                                </TableCell>
                                <TableCell className={`text-right font-mono ${itemVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {itemVariance >= 0 ? '' : '('}{formatCurrency(Math.abs(itemVariance))}{itemVariance < 0 ? ')' : ''}
                                </TableCell>
                                <TableCell className={`text-right text-sm ${itemPercent > 100 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                  {itemPercent.toFixed(0)}%
                                </TableCell>
                                {canEdit && (
                                  <TableCell>
                                    <div className="flex gap-1 justify-end">
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(item)}>
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => deleteItem(item.id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                          
                          {/* Add item prompt if category is empty */}
                          {isExpanded && items.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-4 text-muted-foreground">
                                <Button variant="ghost" onClick={() => openAddDialog(category.id)} className="gap-2">
                                  <Plus className="w-4 h-4" />
                                  Add first item to {category.name}
                                </Button>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Grand Total Row */}
                    <TableRow className="bg-[#70170F]/5 font-semibold border-t-2 border-[#70170F]/20">
                      <TableCell></TableCell>
                      <TableCell className="text-[#70170F]">TOTAL MONTHLY EXPENSES</TableCell>
                      <TableCell className="text-right font-mono text-[#70170F]">{formatCurrency(grandTotals.budgeted)}</TableCell>
                      <TableCell className="text-right font-mono text-[#70170F]">{formatCurrency(grandTotals.actual)}</TableCell>
                      <TableCell className={`text-right font-mono ${grandTotals.variance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {grandTotals.variance >= 0 ? '' : '('}{formatCurrency(Math.abs(grandTotals.variance))}{grandTotals.variance < 0 ? ')' : ''}
                      </TableCell>
                      <TableCell className={`text-right ${grandTotals.percentUsed > 100 ? 'text-red-600' : ''}`}>
                        {grandTotals.percentUsed.toFixed(0)}%
                      </TableCell>
                      {canEdit && <TableCell></TableCell>}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
          
          {/* Project Budgets */}
          <TabsContent value="project" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Project Budgets</CardTitle>
                <CardDescription>Budget tracking synced from project estimates</CardDescription>
              </CardHeader>
              <CardContent>
                {projects.length > 0 ? (
                  <div className="space-y-3">
                    {projects.slice(0, 8).map(project => {
                      const budget = parseFloat(project.estimated_budget || 0);
                      const actual = project.actual_cost || 0;
                      const variance = budget - actual;
                      const percentUsed = budget ? (actual / budget) * 100 : 0;
                      
                      return (
                        <div 
                          key={project.id}
                          className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => navigate(`/projects/${project.id}/hub?tab=financials`)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{project.name || project.project_name}</p>
                            <p className="text-xs text-muted-foreground">{project.project_code || 'No code'} • {project.status || 'Active'}</p>
                          </div>
                          
                          <div className="w-32 hidden md:block">
                            <Progress value={Math.min(percentUsed, 100)} className={`h-2 ${variance < 0 ? '[&>div]:bg-red-500' : ''}`} />
                            <p className="text-xs text-muted-foreground mt-1 text-center">{percentUsed.toFixed(0)}% used</p>
                          </div>
                          
                          <div className="text-right w-24">
                            <p className="font-mono font-medium">{formatCurrency(budget)}</p>
                            <p className="text-xs text-muted-foreground">budget</p>
                          </div>
                          
                          <div className="text-right w-24 hidden sm:block">
                            <p className="font-mono font-medium">{formatCurrency(actual)}</p>
                            <p className="text-xs text-muted-foreground">actual</p>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No projects found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Personal Budgets */}
          <TabsContent value="personal" className="space-y-4">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3 text-sm">
              <EyeOff className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-800"><strong>Private:</strong> Only you can see this data.</span>
            </div>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-medium">My Personal Budget</CardTitle>
                    <CardDescription>Track your personal finances privately</CardDescription>
                  </div>
                  <Button className="bg-[#70170F] hover:bg-[#70170F]/90" onClick={() => openAddDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {budgetItems.length > 0 ? (
                  <div className="space-y-2">
                    {budgetItems.map(item => (
                      <div key={item.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/30">
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono">{formatCurrency(item.actual)} / {formatCurrency(item.budgeted)}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(item)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteItem(item.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Start Your Personal Budget</p>
                    <p className="text-sm mt-1">Track your personal expenses privately</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Budget Item' : 'Add Budget Entry'}
            </DialogTitle>
            <DialogDescription>
              {activeTab === 'company' ? 'Company budget item' : activeTab === 'project' ? 'Project budget' : 'Personal budget'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={v => setFormData(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-muted-foreground">{cat.code}</span>
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.category && (
              <div>
                <Label>Account Code</Label>
                <Select value={formData.subcategory} onValueChange={v => setFormData(prev => ({ ...prev, subcategory: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select account (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.find(c => c.id === formData.category)?.accounts.map(acc => (
                      <SelectItem key={acc.code} value={acc.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">{acc.code}</span>
                          {acc.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Description *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Studio Rent, ChatGPT Plus"
                className="mt-1"
              />
            </div>
            
            {/* Frequency Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency *</Label>
                <Select value={formData.frequency} onValueChange={v => setFormData(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex flex-col">
                          <span>{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Month selector for non-monthly expenses */}
              {formData.frequency !== 'monthly' && (
                <div>
                  <Label>
                    {formData.frequency === 'one_time' ? 'Occurs In' : 
                     formData.frequency === 'annual' ? 'Annual Month' : 'First Quarter Month'}
                  </Label>
                  <Select value={formData.occurMonth} onValueChange={v => setFormData(prev => ({ ...prev, occurMonth: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>
                  {formData.frequency === 'monthly' ? 'Monthly Amount *' : 
                   formData.frequency === 'one_time' ? 'One-Time Amount *' :
                   formData.frequency === 'quarterly' ? 'Quarterly Amount *' : 'Annual Amount *'}
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={formData.budgeted}
                    onChange={e => setFormData(prev => ({ ...prev, budgeted: e.target.value }))}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
                {/* Show annual impact */}
                {formData.budgeted && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Annual: {formatCurrency(
                      formData.frequency === 'monthly' ? parseFloat(formData.budgeted) * 12 :
                      formData.frequency === 'quarterly' ? parseFloat(formData.budgeted) * 4 :
                      parseFloat(formData.budgeted)
                    )}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Actual (this period)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={formData.actual}
                    onChange={e => setFormData(prev => ({ ...prev, actual: e.target.value }))}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="mt-1"
              />
            </div>
            
            {/* Receipt/Document Upload */}
            <div>
              <Label>Receipt / Document (optional)</Label>
              {formData.receiptPreview ? (
                <div className="relative mt-2">
                  <img 
                    src={formData.receiptPreview} 
                    alt="Receipt preview" 
                    className="w-full max-h-32 object-contain rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-7 w-7"
                    onClick={clearReceipt}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-gray-300 hover:border-[#BC5D41] hover:bg-gray-50">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleReceiptFile}
                    />
                    <Camera className="w-4 h-4 text-[#BC5D41]" />
                    <span className="text-sm">Take Photo</span>
                  </label>
                  
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-gray-300 hover:border-[#BC5D41] hover:bg-gray-50">
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      onChange={handleReceiptFile}
                    />
                    <Upload className="w-4 h-4 text-[#BC5D41]" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); clearReceipt(); }}>
              Cancel
            </Button>
            <Button 
              onClick={saveItem} 
              disabled={saving}
              className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingItem ? 'Update' : 'Add Entry'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog - Manage Accounting Codes */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Manage Accounting Codes
            </DialogTitle>
            <DialogDescription>
              Add custom accounting categories and codes for your business
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Existing Categories */}
            <div>
              <Label className="text-xs text-muted-foreground">CURRENT CATEGORIES</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {allCategories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[#70170F]">{cat.code}</span>
                      <span>{cat.name}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{cat.accounts?.length || 0} accounts</Badge>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Add New Category */}
            <div className="border-t pt-4">
              <Label className="text-xs text-muted-foreground">ADD NEW CATEGORY</Label>
              <div className="mt-2 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Code (e.g., 6700)</Label>
                    <Input
                      value={newCategoryForm.code}
                      onChange={e => setNewCategoryForm(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="6700"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={newCategoryForm.name}
                      onChange={e => setNewCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Professional Services"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Sub-accounts (comma separated: code-name)</Label>
                  <Textarea
                    value={newCategoryForm.accounts}
                    onChange={e => setNewCategoryForm(prev => ({ ...prev, accounts: e.target.value }))}
                    placeholder="6710-Legal, 6720-Accounting, 6730-Consulting"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <Button onClick={addCustomCategory} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
